import { ReactElement, ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

import {
  Box,
  Button,
  Paper,
  Popper,
  PopperPlacementType,
  Portal,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Instance as PopperInstance } from '@popperjs/core';

export type TourStep = {
  targetId: string;
  title: string;
  body: ReactNode;
  placement?: PopperPlacementType;
  // Id of a sticky element that overlays the top of the target. The cut-out is the target's
  // exact bounding box, so anything painted over that box — a sticky bar the target scrolls
  // under — lands inside the hole and reads as highlighted. Naming it here starts the
  // spotlight below it instead, leaving the bar dimmed like the rest of the page. An id that
  // resolves to nothing is ignored, the same as a missing target.
  clearBelowId?: string;
};

export type OnboardingTourProps = {
  open: boolean;
  steps: TourStep[];
  onClose: () => void;
  // Called instead of `onClose` when no step could be shown at all, so the caller can tell a
  // user dismissal from a tour whose targets are simply not on screen (e.g. the sidebar on a
  // closed mobile drawer) and keep its once-per-user flag unconsumed. Defaults to `onClose`.
  onUnavailable?: () => void;
};

type Rect = { top: number; left: number; width: number; height: number };

// No breathing room around the cut-out: the amount bar sits flush against the sidebar
// (left) and the viewport bottom, so any padding would spill onto neighbours instead of
// empty space. The hole hugs the target's exact bounds.
const SPOTLIGHT_PADDING = 0;

const SCROLLABLE_OVERFLOW = /(auto|scroll|overlay)/;

/**
 * Scrollable ancestors of the target, innermost first. Locking `body` is not enough: a target
 * inside its own scroll container (the sidebar's nav list, a tab panel) would still scroll out
 * from under the spotlight.
 */
function getScrollableAncestors(element: HTMLElement): HTMLElement[] {
  const ancestors: HTMLElement[] = [];

  for (
    let node = element.parentElement;
    node && node !== document.body;
    node = node.parentElement
  ) {
    const { overflowX, overflowY } = window.getComputedStyle(node);
    const scrolls =
      (SCROLLABLE_OVERFLOW.test(overflowY) && node.scrollHeight > node.clientHeight) ||
      (SCROLLABLE_OVERFLOW.test(overflowX) && node.scrollWidth > node.clientWidth);
    if (scrolls) ancestors.push(node);
  }

  return ancestors;
}

export function OnboardingTour({
  open,
  steps,
  onClose,
  onUnavailable,
}: OnboardingTourProps): ReactElement | null {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  // Bottom edge of `clearBelowId`, remeasured with the target: the bar is sticky, so it moves
  // and resizes independently of what it covers (its text changes as a card is picked up).
  const [clearBelow, setClearBelow] = useState(0);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<PopperInstance>(null);
  // Whether any step of this run found its target, i.e. whether the user was shown anything.
  const hasShownStep = useRef(false);
  const titleId = useId();
  const bodyId = useId();

  const step = steps[activeStep];
  const targetId = step?.targetId;
  const clearBelowId = step?.clearBelowId;
  const isLast = activeStep >= steps.length - 1;
  const handleUnavailable = onUnavailable ?? onClose;

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    hasShownStep.current = false;
  }, [open]);

  // Depend on the primitive `targetId`, not the `step` object: a consumer that defines
  // `steps` inline gives every render a fresh `step` identity, which would re-run this
  // effect every frame (setRect → re-render → re-run), restarting scrollIntoView and the
  // measure loop. Keying on the string keeps it stable regardless of how steps is built.
  useEffect(() => {
    if (!open || !targetId) return undefined;

    const el = document.getElementById(targetId);
    if (!el) {
      if (activeStep < steps.length - 1) setActiveStep((prev) => prev + 1);
      // Out of steps: a run that showed something is done, a run that showed nothing could not
      // be displayed at all and must not consume the once-per-user flag.
      else if (hasShownStep.current) onClose();
      else handleUnavailable();
      return undefined;
    }

    // Instant, not smooth: the spotlight and the tooltip are measured from the target's
    // viewport rect, so an animated scroll would drag them across the screen behind it.
    el.scrollIntoView({ block: 'center', behavior: 'auto' });
    hasShownStep.current = true;

    // The anchor element doesn't change while a step is active — set it once, then keep its
    // rect in sync with the measure loop below.
    setAnchorEl(el);

    // Measured per frame while the step is on screen, and committed only when the rect really
    // changed. A ResizeObserver plus a scroll listener misses the case that actually bites:
    // the target keeps its size and its scroll offset but *moves*, because something else on
    // the page reflowed — the sidebar section expanding for this very tour, a banner mounting,
    // a `Collapse` animating. Polling one `getBoundingClientRect` per frame for the few seconds
    // a tour is open costs nothing and can't miss a shift.
    let raf = 0;
    let previous: Rect | null = null;
    // `null`, not 0, for the same reason `previous` above is: it means "not measured in this
    // step yet". Seeded with 0 it matched the 0 a step without `clearBelowId` measures, so the
    // commit was skipped and the previous step's clamp stayed in state — spotlighting a later
    // target too low, or, once its whole box sat above the stale edge, collapsing the cut-out
    // to nothing and dimming the screen with no hole in it.
    let previousClearBelow: number | null = null;
    const measure = () => {
      // The target can be unmounted from under an open tour (a responsive drawer closing, a tab
      // switching). Its rect then collapses to zero, which would dim the whole screen around an
      // empty hole, so stop instead of spotlighting nothing. Always "unavailable", never a
      // dismissal: the feature stopped being visible, which is exactly the case that must not
      // consume the once-per-user flag, even though an earlier step of this run was shown.
      if (!el.isConnected) {
        handleUnavailable();
        return;
      }

      const { top, left, width, height } = el.getBoundingClientRect();
      if (
        !previous ||
        previous.top !== top ||
        previous.left !== left ||
        previous.width !== width ||
        previous.height !== height
      ) {
        previous = { top, left, width, height };
        setRect(previous);
      }

      const obstruction = clearBelowId ? document.getElementById(clearBelowId) : null;
      const bottom = obstruction ? obstruction.getBoundingClientRect().bottom : 0;
      if (bottom !== previousClearBelow) {
        previousClearBelow = bottom;
        setClearBelow(bottom);
      }
      raf = requestAnimationFrame(measure);
    };
    measure();

    return () => cancelAnimationFrame(raf);
  }, [open, activeStep, clearBelowId, steps.length, targetId, onClose, handleUnavailable]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  // popper.js only repositions on scroll and resize, so a target that moves because the page
  // reflowed would leave the tooltip behind while the spotlight follows.
  useEffect(() => {
    popperRef.current?.update();
  }, [rect]);

  // Move focus into the tooltip so keyboard and screen-reader users land on the step, and again
  // on each step change so the new title is announced. It has to happen from the callback ref,
  // not an effect: MUI's `Portal` mounts its children one commit after this component renders
  // them, so an effect keyed on the anchor runs while `paperRef` is still null — and it would
  // never re-run, leaving focus on the page behind the overlay.
  const setPaperNode = useCallback((node: HTMLDivElement | null) => {
    paperRef.current = node;
    node?.focus();
  }, []);

  useEffect(() => {
    paperRef.current?.focus();
  }, [activeStep]);

  // A tour is modal, so nothing must move under it: scrolling the page — or the target's own
  // scroll container, such as the sidebar's nav list — would drag the target out from under the
  // spotlight. Locked per step, once the target is known (never for a tour that turns out to be
  // unavailable). Programmatic scrolling still works while locked, so `scrollIntoView` can keep
  // centring later steps.
  useEffect(() => {
    if (!open || !anchorEl) return undefined;

    const { body } = document;
    const locked = [body, ...getScrollableAncestors(anchorEl)].map((element) => ({
      element,
      overflow: element.style.overflow,
      paddingRight: element.style.paddingRight,
    }));

    // Only the page scrollbar leaves a gap worth compensating (MUI's Modal does the same); the
    // inner containers use a 4px overlay-style scrollbar, and the measure loop absorbs the shift.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
    locked.forEach(({ element }) => {
      element.style.overflow = 'hidden';
    });

    return () => {
      locked.forEach(({ element, overflow, paddingRight }) => {
        element.style.overflow = overflow;
        element.style.paddingRight = paddingRight;
      });
    };
  }, [open, anchorEl]);

  const handleNext = useCallback(() => {
    if (isLast) onClose();
    else setActiveStep((prev) => prev + 1);
  }, [isLast, onClose]);

  const handleBack = useCallback(() => setActiveStep((prev) => Math.max(0, prev - 1)), []);

  if (!open || !step || !rect || !anchorEl) return null;

  // Clamp the (optionally padded) spotlight to the viewport so it never overshoots a
  // screen edge and stays flush with whichever edge the target hugs, and below any sticky
  // bar the step declared so the bar stays dimmed instead of sitting inside the hole.
  const spotlightTop = Math.max(0, clearBelow, rect.top - SPOTLIGHT_PADDING);
  const spotlightLeft = Math.max(0, rect.left - SPOTLIGHT_PADDING);
  const spotlightWidth =
    Math.min(window.innerWidth, rect.left + rect.width + SPOTLIGHT_PADDING) - spotlightLeft;
  const spotlightHeight = Math.max(
    0,
    Math.min(window.innerHeight, rect.top + rect.height + SPOTLIGHT_PADDING) - spotlightTop
  );

  return (
    <Portal>
      <Box
        aria-hidden
        sx={{
          position: 'fixed',
          top: spotlightTop,
          left: spotlightLeft,
          width: spotlightWidth,
          height: spotlightHeight,
          borderRadius: 1,
          boxShadow: `0 0 0 9999px ${alpha(theme.palette.common.black, 0.6)}`,
          zIndex: theme.zIndex.modal,
          pointerEvents: 'none',
          transition: 'top 0.2s ease, left 0.2s ease, width 0.2s ease, height 0.2s ease',
        }}
      />
      <Popper
        open
        anchorEl={anchorEl}
        modifiers={[
          { name: 'offset', options: { offset: [0, 12] } },
          { name: 'flip', enabled: true },
          // `altAxis` as well as the main one: popper only clamps the main axis by default,
          // which for a `bottom` placement is the horizontal one, so a target too tall for
          // the tooltip to fit under it left the tooltip hanging off the bottom of the
          // viewport — and `flip` kept it there, because the other side had no room either.
          { name: 'preventOverflow', options: { padding: 8, altAxis: true } },
        ]}
        placement={step.placement ?? 'bottom'}
        popperRef={popperRef}
        style={{ zIndex: theme.zIndex.modal + 1 }}
      >
        <Paper
          ref={setPaperNode}
          aria-describedby={bodyId}
          aria-labelledby={titleId}
          elevation={8}
          role="dialog"
          sx={{ p: 2, maxWidth: 320, width: 'calc(100vw - 32px)' }}
          tabIndex={-1}
        >
          <Stack spacing={1.5}>
            <Typography fontWeight="bold" id={titleId} variant="subtitle1">
              {step.title}
            </Typography>
            <Typography color="text.secondary" id={bodyId} variant="body2">
              {step.body}
            </Typography>
            <Stack
              alignItems="center"
              direction="row"
              flexWrap="wrap"
              gap={1}
              justifyContent="space-between"
            >
              <Typography color="text.secondary" variant="caption">
                {activeStep + 1}/{steps.length}
              </Typography>
              <Stack direction="row" flexWrap="wrap" spacing={1}>
                <Button color="inherit" size="small" onClick={onClose}>
                  Skip
                </Button>
                {activeStep > 0 && (
                  <Button size="small" onClick={handleBack}>
                    Back
                  </Button>
                )}
                <Button size="small" variant="contained" onClick={handleNext}>
                  {isLast ? 'Done' : 'Next'}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Popper>
    </Portal>
  );
}

export default OnboardingTour;
